import React, { useEffect, useState } from 'react';
import { Typography, TextField, Tooltip, Box, Fade, Chip } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { Button } from 'src/components/Button';
import { ReactComponent as LeftArrayIcon } from 'src/icons/leftArrowIcon.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import {
  CalendarWrapper,
  HeaderBlock,
  HeaderTitle,
  ParametrsButton,
} from '../styled';
import { DashboardForm, DashboardHeaderProps, InputName } from '../types';

import CustomCalendar from './CustomCalendar';
import { ParametrsMobileContent } from './ParametrsMobileContent';
import ParametrsModal from './ParametrsModal';

export const DashboardHeader = ({
  isSmallScreen,
  title,
  googleAdsMetrics,
  shopifyFilterValues,
  magentoFilterValues,
  connectors,
  onBackButtonClick,
  onTitleChange,
  titleInputRef,
  onTitleSave,
  showTitleSaved,
}: DashboardHeaderProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingTitle, setEditingTitle] = useState(title);
  const [originalTitle, setOriginalTitle] = useState(title);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setEditingTitle(title);
      setOriginalTitle(title);
    }
  }, [title, isEditing]);

  const { reset, watch } = useFormContext<DashboardForm>();

  const handleSaveClick = () => {
    reset(watch());
    setIsModalOpen(false);
  };

  const handleBackClick = () => {
    reset();
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!isSmallScreen && isModalOpen) setIsModalOpen(false);
  }, [isModalOpen, isSmallScreen]);

  const handleBlur = () => {
    setIsEditing(false);
    const trimmedTitle = editingTitle.trim();
    if (onTitleChange) onTitleChange(trimmedTitle);
    if (onTitleSave && trimmedTitle && trimmedTitle !== originalTitle) {
      onTitleSave(trimmedTitle);
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditingTitle(newValue);
    if (onTitleChange) onTitleChange(newValue);
    setShowTooltip(newValue.length > 30);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedTitle = editingTitle.trim();
      console.log('handleKeyDown Enter:', {
        trimmedTitle,
        originalTitle,
        onTitleSave: !!onTitleSave,
      });
      if (onTitleSave && trimmedTitle && trimmedTitle !== originalTitle) {
        console.log('Calling onTitleSave with:', trimmedTitle);
        onTitleSave(trimmedTitle);
      } else {
        console.log('Not calling onTitleSave:', {
          hasOnTitleSave: !!onTitleSave,
          hasTrimmedTitle: !!trimmedTitle,
          isDifferent: trimmedTitle !== originalTitle,
        });
      }
      titleInputRef?.current?.blur();
    } else if (e.key === 'Escape') {
      setEditingTitle(originalTitle);
      titleInputRef?.current?.blur();
    }
  };

  return (
    <HeaderBlock>
      <Button
        variant="text"
        sx={(theme) => ({
          color: theme.palette.primary.contrastText,
        })}
        onClick={onBackButtonClick}
      >
        <LeftArrayIcon />
        BACK
      </Button>
      {onTitleChange ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            position: 'relative',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Tooltip
            title={editingTitle}
            open={showTooltip && editingTitle.length > 30}
            placement="top"
            arrow
          >
            <TextField
              value={editingTitle}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              inputRef={titleInputRef}
              variant="standard"
              sx={{
                fontSize: { xs: '16px', sm: '18px' },
                fontWeight: 600,
                minWidth: { xs: 120, sm: 160 },
                maxWidth: '100%',
                '& .MuiInput-root': {
                  fontSize: { xs: 16, sm: 18 },
                  fontWeight: 600,
                  '&:before': {
                    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
                  },
                  '&:after': {
                    borderBottom: '2px solid #1976d2',
                  },
                },
                '& .MuiInput-input': {
                  padding: { xs: '4px 0', sm: '6px 0' },
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                },
              }}
              inputProps={{ maxLength: 200 }}
            />
          </Tooltip>
          <Fade in={showTitleSaved && !isEditing} timeout={200}>
            <CheckCircleIcon
              sx={{
                color: 'success.main',
                fontSize: 18,
                opacity: 0.8,
              }}
            />
          </Fade>
        </Box>
      ) : (
        <HeaderTitle>{title}</HeaderTitle>
      )}
      <CalendarWrapper>
        {isSmallScreen ? (
          <ParametrsButton onClick={() => setIsModalOpen(true)}>
            Parameters
          </ParametrsButton>
        ) : (
          <>
            <Typography
              fontSize={{ xs: '14px', sm: '16px', md: '18px' }}
              fontWeight={400}
              lineHeight="24px"
              sx={{
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              From
            </Typography>
            <CustomCalendar name={InputName.startDate} />
            <Typography
              fontSize={{ xs: '14px', sm: '16px', md: '18px' }}
              fontWeight={400}
              lineHeight="24px"
              sx={{
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              to
            </Typography>
            <CustomCalendar name={InputName.endDate} />
          </>
        )}
      </CalendarWrapper>
      {isSmallScreen && (
        <ParametrsModal
          open={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
        >
          {isModalOpen && (
            <ParametrsMobileContent
              googleAdsMetrics={googleAdsMetrics}
              shopifyFilterValues={shopifyFilterValues}
              magentoFilterValues={magentoFilterValues}
              connectors={connectors}
              onBackClick={handleBackClick}
              onSave={handleSaveClick}
            />
          )}
        </ParametrsModal>
      )}
    </HeaderBlock>
  );
};
